import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NotebookPen, Upload, FileArchive, Loader2, ArrowRight, GitBranch, AlertCircle, Github, Search } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MultiSourceDropzone from "../components/MultiSourceDropzone";

export default function KnuthNewProject() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState("upload"); // 'upload' | 'github'
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | creating | uploading | analyzing | importing

  // GitHub state
  const [gh, setGh] = useState({ connected: false, loading: true, repos: [], filter: "", branch: "", selected: null });

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  useEffect(() => {
    if (mode !== "github" || gh.repos.length > 0) return;
    setGh((g) => ({ ...g, loading: true }));
    api.get("/github/repos")
      .then(({ data }) => setGh((g) => ({ ...g, connected: true, loading: false, repos: data || [] })))
      .catch((e) => {
        const notConnected = e?.response?.data?.detail?.includes("not connected");
        setGh((g) => ({ ...g, connected: !notConnected, loading: false }));
      });
  }, [mode, gh.repos.length]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) setFiles(dropped);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Project name must be at least 2 characters");
      return;
    }
    if (files.length === 0) {
      toast.error("Add at least one file (or a ZIP of your project)");
      return;
    }

    setBusy(true);
    setPhase("creating");
    try {
      const { data: project } = await api.post("/knuth/projects", { name: name.trim(), description: description.trim() });

      setPhase("uploading");
      const form = new FormData();
      files.forEach((f) => form.append("files", f));

      const token = localStorage.getItem("dpa_token");
      const res = await fetch(`${API}/knuth/projects/${project.id}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      setPhase("analyzing");
      const analyzed = await res.json();
      toast.success(`Project ready — ${analyzed.understanding?.file_count || 0} files indexed`);
      nav(`/knuth/projects/${project.id}`);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
      setBusy(false);
      setPhase("idle");
    }
  };

  const importRepo = async () => {
    if (!gh.selected) {
      toast.error("Pick a repository first");
      return;
    }
    setBusy(true);
    setPhase("importing");
    try {
      const { data } = await api.post("/knuth/projects/import-github", {
        full_name: gh.selected.full_name,
        branch: gh.branch || gh.selected.default_branch,
        project_name: name.trim() || gh.selected.name,
      });
      toast.success(`Imported · ${data.understanding?.file_count || 0} files indexed`);
      nav(`/knuth/projects/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
      setBusy(false);
      setPhase("idle");
    }
  };

  const totalMB = (files.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(2);

  return (
    <div className="container-narrow py-10 md:py-14 max-w-3xl" data-testid="knuth-new-project">
      <div className="mb-8">
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
          <NotebookPen className="w-3 h-3" /> New Knuth project
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-semibold">Give Knuth a codebase.</h1>
        <p className="text-ink2 mt-2 text-sm">Drop a ZIP of your repository, or select individual files. Knuth walks the tree, filters heavy binaries, and indexes everything readable.</p>
      </div>

      <form onSubmit={submit} className="card-soft p-8 space-y-6" data-testid="knuth-new-form">
        <div>
          <label className="text-sm font-medium block mb-1">Project name</label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Adventure Notes iOS App"
            className="input-soft"
            data-testid="knuth-name-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Short description <span className="text-ink2 font-normal">(optional)</span></label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One line of context — what does this software do?"
            rows={2}
            className="input-soft"
            data-testid="knuth-desc-input"
          />
        </div>

        {/* Mode tabs */}
        <div className="flex gap-2" data-testid="knuth-source-tabs">
          <button
            type="button"
            onClick={() => setMode("upload")}
            className={`flex-1 rounded-full py-2 text-sm font-semibold border transition inline-flex items-center justify-center gap-2 ${mode === "upload" ? "bg-ink text-cream border-ink" : "bg-cream text-ink2 border-border hover:border-ink"}`}
            data-testid="knuth-tab-upload"
          >
            <Upload className="w-4 h-4" /> Upload files
          </button>
          <button
            type="button"
            onClick={() => setMode("github")}
            className={`flex-1 rounded-full py-2 text-sm font-semibold border transition inline-flex items-center justify-center gap-2 ${mode === "github" ? "bg-ink text-cream border-ink" : "bg-cream text-ink2 border-border hover:border-ink"}`}
            data-testid="knuth-tab-github"
          >
            <Github className="w-4 h-4" /> Connect GitHub
          </button>
        </div>

        {/* Drop zone (upload mode) */}
        {mode === "upload" && (
        <div>
          <label className="text-sm font-medium block mb-2">Source files</label>
          <MultiSourceDropzone files={files} onFiles={setFiles} data-testid="knuth-drop-zone" />
        </div>
        )}

        {/* GitHub picker (github mode) */}
        {mode === "github" && (
          <div data-testid="knuth-github-picker">
            {gh.loading ? (
              <div className="rounded-2xl border border-border bg-paper/40 p-8 text-center text-sm text-ink2">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading your repositories…
              </div>
            ) : !gh.connected ? (
              <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center" data-testid="knuth-github-not-connected">
                <Github className="w-10 h-10 text-ink mx-auto mb-3" />
                <h3 className="font-heading text-xl font-semibold mb-1">Connect your GitHub</h3>
                <p className="text-sm text-ink2 mb-4">Grants read-only access so Knuth can index the repo you pick.</p>
                <a
                  href={`${API}/github/oauth/start`}
                  className="btn-primary text-sm inline-flex"
                  data-testid="knuth-github-connect"
                >
                  <Github className="w-4 h-4" /> Connect GitHub <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ) : (
              <>
                <label className="text-sm font-medium block mb-2">Pick a repository</label>
                <div className="rounded-2xl border border-border overflow-hidden bg-paper/40">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                    <Search className="w-3.5 h-3.5 text-ink2" />
                    <input
                      value={gh.filter}
                      onChange={(e) => setGh((g) => ({ ...g, filter: e.target.value }))}
                      placeholder="Filter repositories…"
                      className="flex-1 text-sm bg-transparent outline-none"
                      data-testid="knuth-gh-filter"
                    />
                    <span className="text-xs text-ink2">{gh.repos.length} repos</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-border">
                    {gh.repos
                      .filter((r) => !gh.filter || r.full_name.toLowerCase().includes(gh.filter.toLowerCase()))
                      .map((r) => {
                        const sel = gh.selected?.id === r.id;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setGh((g) => ({ ...g, selected: r, branch: r.default_branch }))}
                            className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-3 transition ${sel ? "bg-teal/15" : "hover:bg-cream"}`}
                            data-testid={`gh-repo-${r.id}`}
                          >
                            <span className="min-w-0 truncate">
                              <span className="font-mono">{r.full_name}</span>
                              {r.private && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-marigold">private</span>}
                            </span>
                            <span className="text-[11px] text-ink2 font-mono flex-shrink-0">{r.default_branch}</span>
                          </button>
                        );
                      })}
                  </div>
                </div>

                {gh.selected && (
                  <div className="mt-3 flex items-end gap-2" data-testid="knuth-gh-branch">
                    <div className="flex-1">
                      <label className="text-xs text-ink2 block mb-1">Branch</label>
                      <input
                        value={gh.branch}
                        onChange={(e) => setGh((g) => ({ ...g, branch: e.target.value }))}
                        placeholder={gh.selected.default_branch}
                        className="input-soft"
                        data-testid="knuth-gh-branch-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={importRepo}
                      disabled={busy}
                      className="btn-primary disabled:opacity-60"
                      data-testid="knuth-gh-import-btn"
                    >
                      {busy && phase === "importing" ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <>Import repo <ArrowRight className="w-3.5 h-3.5" /></>}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Secrets notice */}
        <div className="flex items-start gap-2 text-xs text-ink2" data-testid="knuth-secrets-notice">
          <AlertCircle className="w-4 h-4 text-marigold flex-shrink-0 mt-0.5" />
          <span>Knuth never sends detected <code className="bg-paper px-1 rounded">.env</code>, keys, tokens, or private certs to the AI. Only public source-like files are ever indexed.</span>
        </div>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => nav("/knuth")}
            className="btn-ghost text-sm"
            data-testid="knuth-cancel-btn"
          >
            Cancel
          </button>
          {mode === "upload" && (
            <button
              type="submit"
              disabled={busy}
              className="btn-primary disabled:opacity-60"
              data-testid="knuth-create-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {phase === "creating" && "Creating project…"}
                  {phase === "uploading" && "Uploading files…"}
                  {phase === "analyzing" && "Analyzing codebase…"}
                </>
              ) : (
                <>Create project <ArrowRight className="w-3.5 h-3.5" /></>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
